import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Pencil, Trash2, Save } from "lucide-react";
import { toast } from "sonner";
import { ModuleShell, DataTable, Field, ExportButtons } from "@/components/partners/common";
import { EditDialog } from "@/components/partners/ProfileTabs";
import { Sel } from "@/components/partners/PartnerForm";
import { fetchMeta, titleCase, apiError, KIND } from "@/lib/partnerApi";
import { CURRENCIES } from "@/lib/currency";

export function DealerPriceLists() {
  const [meta, setMeta] = useState(null);
  const [level, setLevel] = useState("");
  const [rows, setRows] = useState([]);
  const [items, setItems] = useState([]);
  const [dealers, setDealers] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = useCallback(() => {
    api.get("/dealer-master/pricing", { params: { price_level: level || undefined } }).then((r) => setRows(r.data)).catch((e) => toast.error(apiError(e)));
  }, [level]);
  useEffect(() => {
    fetchMeta("dealer").then(setMeta).catch(() => {});
    api.get("/items").then((r) => setItems(r.data)).catch(() => {});
    api.get("/dealer-master").then((r) => setDealers(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  const byId = Object.fromEntries(dealers.map((d) => [d.id, d]));
  const canPrice = (meta?.my_actions || []).includes("change_pricing");
  const save = async (f) => {
    const body = { ...f };
    ["id", "item_code", "item_name", "effective_now", "is_active", "created_at", "created_by", "updated_at", "updated_by"].forEach((k) => delete body[k]);
    if (!body.dealer_id) body.dealer_id = null;
    if (edit?.id) await api.put(`/dealer-master/pricing/${edit.id}`, body); else await api.post("/dealer-master/pricing", body);
    toast.success("Price saved"); load();
  };
  const remove = async (r) => {
    if (!window.confirm(`Retire price for ${r.item_code}?`)) return;
    try { await api.delete(`/dealer-master/pricing/${r.id}`); load(); } catch (e) { toast.error(apiError(e)); }
  };
  const cols = [
    { key: "applies_to", label: "Dealer / Level", render: (r) => (r.dealer_id ? `${byId[r.dealer_id]?.code || ""} ${byId[r.dealer_id]?.name || "Dealer"}` : `Level: ${titleCase(r.price_level)}`) },
    { key: "item_code", label: "Item" }, { key: "item_name", label: "Description" },
    { key: "standard_price", label: "Standard", type: "money" }, { key: "dealer_price", label: "Dealer Price", type: "money" },
    { key: "discount_pct", label: "Disc %", type: "number" }, { key: "special_price", label: "Special", type: "money" },
    { key: "tax_pct", label: "Tax %", type: "number" }, { key: "min_qty", label: "Min", type: "number" }, { key: "max_qty", label: "Max", type: "number" },
    { key: "effective_from", label: "From", type: "date" }, { key: "effective_to", label: "To", type: "date" },
    { key: "effective_now", label: "Live", render: (r) => (r.effective_now ? "Yes" : "No") },
    ...(canPrice ? [{ key: "_a", label: "", render: (r) => (
      <div className="flex justify-end">
        <Button variant="ghost" size="icon" onClick={() => setEdit(r)}><Pencil className="w-4 h-4 text-slate-500" /></Button>
        <Button variant="ghost" size="icon" onClick={() => remove(r)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
      </div>) }] : []),
  ];
  const exportRows = rows.map((r) => ({ ...r, applies_to: r.dealer_id ? byId[r.dealer_id]?.name : `Level: ${titleCase(r.price_level)}` }));
  return (
    <ModuleShell kind="dealer" title="Dealer Price Lists" tid="dealer-price-lists"
      subtitle="Item → Dealer → Price. Price-level lists (Standard Dealer, Premium Dealer, Distributor, Key Account, Special Customer) apply to every dealer on that level; a dealer-specific price overrides its level."
      actions={(
        <>
          <ExportButtons kind="dealer" title="Dealer Price List" columns={cols.filter((c) => !c.key.startsWith("_"))} rows={exportRows} />
          {canPrice && <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => setEdit({ price_level: level || meta?.price_levels?.[0], currency: "INR" })} data-testid="new-price-btn"><Plus className="w-4 h-4 mr-1" /> New Price</Button>}
        </>
      )}>
      <div className="w-64"><Sel value={level} onChange={setLevel} placeholder="All levels & dealers" options={meta?.price_levels || []} /></div>
      <DataTable rows={rows} columns={cols} tid="price-table" empty="No price records." />
      {meta && (
        <EditDialog open={!!edit} onOpenChange={(v) => !v && setEdit(null)} title={edit?.id ? `Edit price ${edit.item_code}` : "New Price"} initial={edit} onSubmit={save}
          fields={[
            { key: "price_level", label: "Price Level (leave blank for a single dealer)", type: "select", options: meta.price_levels },
            { key: "dealer_id", label: "…or a specific Dealer", type: "select", options: dealers.map((d) => ({ value: d.id, label: `${d.code} · ${d.name}` })) },
            { key: "item_id", label: "Item", type: "select", required: true, span: true, options: items.map((i) => ({ value: i.id, label: `${i.code} · ${i.name}` })) },
            { key: "standard_price", label: "Standard Price", type: "number" }, { key: "dealer_price", label: "Dealer Price", type: "number" },
            { key: "discount_pct", label: "Discount %", type: "number" }, { key: "special_price", label: "Special Price", type: "number" },
            { key: "tax_pct", label: "Tax %", type: "number" }, { key: "currency", label: "Currency", type: "select", options: CURRENCIES.map((c) => ({ value: c.code, label: c.label })) },
            { key: "min_qty", label: "Minimum Quantity", type: "number" }, { key: "max_qty", label: "Maximum Quantity", type: "number" },
            { key: "effective_from", label: "Effective From", type: "date" }, { key: "effective_to", label: "Effective To", type: "date" },
          ]} />
      )}
    </ModuleShell>
  );
}

const ROLE_OPTIONS = ["admin", "purchase_manager", "purchase_executive", "sales_manager", "sales_executive", "accounts", "finance_manager",
  "management", "store_manager", "stores", "production_manager", "planner", "quality_inspector", "quality", "sales_crm", "marketing"];

function RolePicker({ value, onChange }) {
  return (
    <div className="flex flex-wrap gap-1">
      {ROLE_OPTIONS.filter((r) => r !== "admin").map((r) => (
        <button key={r} type="button" onClick={() => onChange(value.includes(r) ? value.filter((x) => x !== r) : [...value, r])}
          className={`text-[11px] px-2 py-0.5 rounded-full border ${value.includes(r) ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-200"}`}>{titleCase(r)}</button>
      ))}
    </div>
  );
}

export function PartnerSettings({ kind }) {
  const [cfg, setCfg] = useState(null);
  const [perm, setPerm] = useState(null);
  const [meta, setMeta] = useState(null);
  const [cf, setCf] = useState(null);
  const load = useCallback(async () => {
    try {
      const [a, b] = await Promise.all([api.get("/partner-master/config"), api.get("/partner-master/permissions")]);
      setCfg(a.data); setPerm(b.data);
    } catch (e) { toast.error(apiError(e)); }
  }, []);
  useEffect(() => { load(); fetchMeta(kind).then(setMeta).catch(() => {}); }, [load, kind]);
  if (!cfg || !perm || !meta) return <ModuleShell kind={kind} title="Settings"><div className="text-sm text-slate-500">Loading…</div></ModuleShell>;
  const levels = cfg.workflows[kind];
  const setLevels = (l) => setCfg({ ...cfg, workflows: { ...cfg.workflows, [kind]: l } });
  const saveCfg = async (patch) => {
    try { setCfg((await api.put("/partner-master/config", patch)).data); toast.success("Settings saved"); } catch (e) { toast.error(apiError(e)); }
  };
  const matrix = perm.matrix[kind] || {};
  const toggle = (role, action) => {
    const cur = matrix[role] || [];
    const next = cur.includes(action) ? cur.filter((a) => a !== action) : [...cur, action];
    setPerm({ ...perm, matrix: { ...perm.matrix, [kind]: { ...matrix, [role]: next } } });
  };
  const savePerm = async () => {
    try { const { data } = await api.put("/partner-master/permissions", { matrix: perm.matrix }); setPerm({ ...perm, matrix: data.matrix }); toast.success("Permissions saved"); } catch (e) { toast.error(apiError(e)); }
  };
  const customFields = cfg.custom_fields[kind] || [];
  const alerts = cfg.alerts;
  const setAlert = (k, v) => setCfg({ ...cfg, alerts: { ...alerts, [k]: v } });
  return (
    <ModuleShell kind={kind} title={`${KIND[kind].label} Management Settings`} tid={`${kind}-settings`} subtitle="Administrator only. Changes apply immediately and are recorded in the audit log.">
      <Card title="Approval Workflow" action={<Button size="sm" onClick={() => saveCfg({ workflows: cfg.workflows, bank_approver_roles: cfg.bank_approver_roles, credit_approver_roles: cfg.credit_approver_roles })} data-testid="save-workflow"><Save className="w-4 h-4 mr-1" /> Save</Button>}>
        <div className="text-xs text-slate-500 mb-2">{KIND[kind].label} Created → Submitted → {levels.map((l) => l.name).join(" → ")} → Approved → Active</div>
        <div className="space-y-2">
          {levels.map((lv, i) => (
            <div key={i} className="flex gap-3 items-start border border-slate-200 rounded-md p-2">
              <div className="text-sm font-bold w-6 pt-2">{i + 1}.</div>
              <div className="flex-1 space-y-1.5">
                <Input value={lv.name} onChange={(e) => setLevels(levels.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} className="h-8" data-testid={`level-name-${i}`} />
                <RolePicker value={lv.roles} onChange={(r) => setLevels(levels.map((x, j) => (j === i ? { ...x, roles: r } : x)))} />
              </div>
              <Button variant="ghost" size="icon" disabled={levels.length === 1} onClick={() => setLevels(levels.filter((_, j) => j !== i))}><Trash2 className="w-4 h-4 text-red-500" /></Button>
            </div>
          ))}
          <Button size="sm" variant="outline" onClick={() => setLevels([...levels, { name: "New Level", roles: ["management"] }])}><Plus className="w-4 h-4 mr-1" /> Add Level</Button>
        </div>
        <div className="grid md:grid-cols-2 gap-4 mt-4">
          <Field label="Bank-detail change approvers"><RolePicker value={cfg.bank_approver_roles} onChange={(r) => setCfg({ ...cfg, bank_approver_roles: r })} /></Field>
          {kind === "dealer" && <Field label="Credit-limit override approvers"><RolePicker value={cfg.credit_approver_roles} onChange={(r) => setCfg({ ...cfg, credit_approver_roles: r })} /></Field>}
        </div>
      </Card>

      <Card title="Role & Permission Matrix" action={<Button size="sm" onClick={savePerm} data-testid="save-perms"><Save className="w-4 h-4 mr-1" /> Save</Button>}>
        <div className="overflow-x-auto">
          <table className="text-xs w-full">
            <thead><tr className="text-slate-500"><th className="text-left py-1 pr-2">Role</th>{perm.actions.map((a) => <th key={a} className="px-1 font-medium">{titleCase(a)}</th>)}</tr></thead>
            <tbody>
              <tr className="border-t border-slate-100"><td className="py-1 pr-2 font-semibold">Admin</td>{perm.actions.map((a) => <td key={a} className="text-center">✓</td>)}</tr>
              {ROLE_OPTIONS.filter((r) => r !== "admin").map((role) => (
                <tr key={role} className="border-t border-slate-100">
                  <td className="py-1 pr-2">{titleCase(role)}</td>
                  {perm.actions.map((a) => (
                    <td key={a} className="text-center"><input type="checkbox" checked={(matrix[role] || []).includes(a)} onChange={() => toggle(role, a)} data-testid={`perm-${role}-${a}`} /></td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="text-xs text-slate-500 mt-2">Own-scope roles (see only records assigned to / created by them): {(cfg.own_scope_roles[kind] || []).map(titleCase).join(", ") || "none"}</div>
      </Card>

      {kind === "dealer" && (
        <Card title="Credit Control" action={<Button size="sm" onClick={() => saveCfg({ credit: cfg.credit })}><Save className="w-4 h-4 mr-1" /> Save</Button>}>
          <div className="grid md:grid-cols-3 gap-3">
            <Field label="When Outstanding + Pending + New Order > Credit Limit">
              <Sel value={cfg.credit.mode} onChange={(v) => setCfg({ ...cfg, credit: { ...cfg.credit, mode: v } })} placeholder="—"
                options={[{ value: "warn", label: "Warn and allow" }, { value: "block", label: "Block the order" }, { value: "approval", label: "Block and request approval" }]} tid="credit-mode" />
            </Field>
            <Field label="Count pending (undelivered) orders">
              <Sel value={cfg.credit.include_pending_orders ? "yes" : "no"} onChange={(v) => setCfg({ ...cfg, credit: { ...cfg.credit, include_pending_orders: v === "yes" } })}
                options={[{ value: "yes", label: "Yes" }, { value: "no", label: "No — outstanding only" }]} />
            </Field>
            <Field label="Approved override valid for (days)"><Input type="number" value={cfg.credit.override_valid_days} onChange={(e) => setCfg({ ...cfg, credit: { ...cfg.credit, override_valid_days: Number(e.target.value) } })} /></Field>
          </div>
        </Card>
      )}

      <Card title="Alerts & Notifications" action={<Button size="sm" onClick={() => saveCfg({ alerts })}><Save className="w-4 h-4 mr-1" /> Save</Button>}>
        <div className="grid md:grid-cols-4 gap-3">
          {[["document_expiry_days", "Document expiry warning (days)"], ["contract_expiry_days", "Agreement expiry warning (days)"],
            ["payment_due_days", "Payment due warning (days)"], ["performance_threshold", "Supplier performance threshold (score)"],
            ["quality_rejection_lookback_days", "Quality rejection look-back (days)"], ["inactive_after_days", "Inactive after (days without orders)"],
            ["pending_order_days", "Pending order alert after (days)"]].map(([k2, label]) => (
            <Field key={k2} label={label}><Input type="number" value={alerts[k2]} onChange={(e) => setAlert(k2, Number(e.target.value))} /></Field>
          ))}
          <Field label="Channels">
            <div className="text-xs space-y-1">
              <label className="flex gap-2 items-center"><input type="checkbox" checked disabled /> In-system (bell)</label>
              <label className="flex gap-2 items-center"><input type="checkbox" checked={alerts.email_enabled} onChange={(e) => setAlert("email_enabled", e.target.checked)} /> Email digest</label>
              <label className="flex gap-2 items-center"><input type="checkbox" checked={alerts.whatsapp_enabled} onChange={(e) => setAlert("whatsapp_enabled", e.target.checked)} /> WhatsApp / SMS (gateway env)</label>
            </div>
          </Field>
        </div>
        <div className="mt-3"><Field label={`Alert recipients (${KIND[kind].label.toLowerCase()} alerts)`}>
          <RolePicker value={alerts.recipient_roles[kind] || []} onChange={(r) => setAlert("recipient_roles", { ...alerts.recipient_roles, [kind]: r })} />
        </Field></div>
      </Card>

      <Card title="Required Documents for Approval" action={<Button size="sm" onClick={() => saveCfg({ required_documents: cfg.required_documents })}><Save className="w-4 h-4 mr-1" /> Save</Button>}>
        <div className="flex flex-wrap gap-1">
          {meta.document_types.map((t) => {
            const cur = cfg.required_documents[kind] || [];
            const on = cur.includes(t);
            return (
              <button key={t} type="button" onClick={() => setCfg({ ...cfg, required_documents: { ...cfg.required_documents, [kind]: on ? cur.filter((x) => x !== t) : [...cur, t] } })}
                className={`text-xs px-2.5 py-1 rounded-full border ${on ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-200"}`}>{titleCase(t)}</button>
            );
          })}
        </div>
        <div className="text-xs text-slate-500 mt-2">Final approval is refused until each selected document is uploaded, approved and not expired.</div>
      </Card>

      <Card title="Custom Fields" action={<Button size="sm" variant="outline" onClick={() => setCf({ type: "text" })}><Plus className="w-4 h-4 mr-1" /> Add Field</Button>}>
        <DataTable rows={customFields.map((c, i) => ({ ...c, id: String(i) }))} empty="No custom fields — add one to extend the master without a database change." columns={[
          { key: "key", label: "Key" }, { key: "label", label: "Label" }, { key: "type", label: "Type" },
          { key: "required", label: "Required", render: (r) => (r.required ? "Yes" : "No") }, { key: "options", label: "Options", render: (r) => (r.options || []).join(", ") },
          { key: "_a", label: "", render: (r) => <Button variant="ghost" size="icon" onClick={() => saveCfg({ custom_fields: { ...cfg.custom_fields, [kind]: customFields.filter((_, i) => String(i) !== r.id) } })}><Trash2 className="w-4 h-4 text-red-500" /></Button> },
        ]} />
        <EditDialog open={!!cf} onOpenChange={(v) => !v && setCf(null)} title="Add Custom Field" initial={cf}
          onSubmit={async (f) => {
            const field = { key: (f.key || "").trim().toLowerCase().replace(/[^a-z0-9_]/g, "_"), label: f.label, type: f.type || "text", required: !!f.required,
              options: f.type === "select" ? String(f.options || "").split(",").map((s) => s.trim()).filter(Boolean) : undefined };
            if (!field.key || !field.label) throw Object.assign(new Error("Key and label are required"), { response: { data: { detail: "Key and label are required" } } });
            await saveCfg({ custom_fields: { ...cfg.custom_fields, [kind]: [...customFields, field] } });
          }}
          fields={[{ key: "key", label: "Key (a–z, 0–9, _)", required: true }, { key: "label", label: "Label", required: true },
            { key: "type", label: "Type", type: "select", options: ["text", "number", "date", "select", "boolean"] }, { key: "required", label: "Required", type: "bool" },
            { key: "options", label: "Options (comma-separated, for select)", span: true }]} />
      </Card>
    </ModuleShell>
  );
}

function Card({ title, action, children }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm font-semibold text-slate-700">{title}</div>
        {action}
      </div>
      {children}
    </div>
  );
}
